import {
  analyticsReady,
  isBookingLink,
  setAnalyticsUserId,
  trackEvent,
} from '@/lib/analytics/events'
import { SIGNUP_FLAG_COOKIE, consumeSignUpFlag } from '@/lib/analytics/signup-flag'

/**
 * Browser wiring for the GA4 conversions that don't belong to one button:
 * the one-shot `sign_up` and site-wide booking-link clicks. Client-only.
 */

function readSelectedPlan(): string | null {
  try {
    return window.localStorage.getItem('st.selectedPlan')
  } catch {
    return null
  }
}

/**
 * Fire `sign_up` if /api/auth/complete-profile left its one-shot flag.
 *
 * Waits for gtag rather than consuming blind: a flag consumed before the tag
 * loaded would be gone with nothing sent. Returns true once the flag has been
 * dealt with (sent, or found absent/malformed) so callers can stop retrying.
 * Consent is gtag's call — a rejected visitor's flag is consumed and the hit
 * is dropped by `ga-disable-<ID>`, which is the point.
 */
export function flushSignUpConversion(): boolean {
  if (typeof document === 'undefined') return true
  if (!analyticsReady()) return false
  const flag = consumeSignUpFlag({
    cookieString: document.cookie,
    deleteCookie: () => {
      document.cookie = `${SIGNUP_FLAG_COOKIE}=; Max-Age=0; path=/; SameSite=Lax`
    },
    fallbackPlan: readSelectedPlan(),
  })
  if (!flag) return true
  if (flag.uid) setAnalyticsUserId(flag.uid)
  trackEvent('sign_up', flag.plan ? { method: flag.method, plan: flag.plan } : { method: flag.method })
  return true
}

/**
 * Retry `flushSignUpConversion` while gtag.js finishes loading (it is an
 * `afterInteractive` script, so on a hard load it lands after React's first
 * effects). Returns a cancel function.
 */
export function flushSignUpConversionSoon(attempts = 20, intervalMs = 250): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null
  let left = attempts
  const tick = () => {
    timer = null
    if (flushSignUpConversion()) return
    if (--left > 0) timer = setTimeout(tick, intervalMs)
  }
  tick()
  return () => {
    if (timer) clearTimeout(timer)
  }
}

/**
 * One delegated listener for every booking link on the page.
 *
 * Delegation rather than an onClick per link because the Calendly CTAs live in
 * server components across /pricing, /franchises and the /for/* chrome, and a
 * new one added later should count without anybody remembering to wire it.
 * Capture phase, so a link that stops propagation still counts. Only real
 * (trusted) clicks: the homepage demos drive a scripted cursor.
 */
export function installBookingLinkListener(): () => void {
  if (typeof document === 'undefined') return () => {}
  const onClick = (e: MouseEvent) => {
    if (!e.isTrusted) return
    const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null
    if (!a || !isBookingLink(a.href)) return
    trackEvent('generate_lead', { lead_source: 'calendly', page_path: window.location.pathname })
  }
  document.addEventListener('click', onClick, true)
  // Middle-click / ctrl-click open the booking page too, without a `click`.
  const onAux = (e: MouseEvent) => {
    if (e.button === 1) onClick(e)
  }
  document.addEventListener('auxclick', onAux, true)
  return () => {
    document.removeEventListener('click', onClick, true)
    document.removeEventListener('auxclick', onAux, true)
  }
}
